<script setup lang="ts">
import { computed } from 'vue';

import type {
  EnrichmentRunSummary,
  EnrichmentStatusEntry,
  RefreshCheckedReason,
  RefreshOutcomeDto,
} from '@factoryfixinc/ats-interfaces';

import CheckIcon from '@/assets/svg/check-16.svg?component';
import EnrichmentCheckingPill from '@/components/Common/EnrichmentCheckingPill.vue';
import { bothContactsVerified } from '@/components/Common/trace-panel.mapper';

interface Props {
  outcome: RefreshOutcomeDto | null;
  isLoading?: boolean;
  result?: EnrichmentRunSummary | null;
  fieldStates?: EnrichmentStatusEntry['fields'];
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  result: null,
  fieldStates: undefined,
});

const emit = defineEmits<{
  (e: 'select', result: EnrichmentRunSummary | null): void;
}>();

type ChipState = 'hidden' | 'loading' | 'updated' | 'verified' | 'checked';

// Explicit reason → state mapping. Exhaustive over `RefreshCheckedReason` —
// if a new reason is added to the wire contract without a mapping here,
// TypeScript will flag the missing key.
const REASON_TO_STATE: Record<RefreshCheckedReason, Extract<ChipState, 'verified' | 'checked'>> = {
  recency: 'verified',
  engagement: 'verified',
  no_change: 'verified',
  no_match: 'checked',
  degraded: 'checked',
};

const state = computed<ChipState>(() => {
  if (props.isLoading) return 'loading';
  if (!props.outcome) return 'hidden';
  if (props.outcome.outcome === 'updated') return 'updated';
  // A server-reported in-progress check reuses the same spinner affordance as the
  // isLoading prop; narrowing it out here also leaves `outcome` as the 'checked'
  // variant so `.reason` below is well-typed.
  if (props.outcome.outcome === 'checking') return 'loading';

  // A no_change run only verified what its own checks confirmed; same rule as the panel.
  if (props.outcome.reason === 'no_change' && !bothContactsVerified(props.fieldStates)) {
    return 'checked';
  }

  // The `Record<RefreshCheckedReason, ...>` typing guarantees a mapping at
  // compile time, but `?? 'hidden'` is a runtime safety net in case a new wire
  // reason ships before this FE is redeployed — better to render nothing than
  // a half-broken chip with an icon and no label.
  return REASON_TO_STATE[props.outcome.reason] ?? 'hidden';
});

const label = computed(() => {
  switch (state.value) {
    case 'loading':
      return 'Checking...';
    case 'updated':
      return 'Updated just now';
    case 'verified':
      return 'Verified just now';
    case 'checked':
      return 'Checked just now';
    default:
      return '';
  }
});

const sourcesChecked = computed(
  () =>
    (props.outcome?.outcome !== 'checking' ? props.outcome?.sourcesChecked : undefined) ??
    props.result?.evidence.sourcesChecked,
);
const isInteractive = computed(() => state.value !== 'hidden' && state.value !== 'loading');
const tooltip = computed(() => {
  if (!isInteractive.value) return undefined;
  if (!sourcesChecked.value) return 'What we checked';

  const sourceLabel = sourcesChecked.value === 1 ? 'source' : 'sources';
  return `What we checked · ${sourcesChecked.value} ${sourceLabel} checked`;
});

function handleClick(): void {
  emit('select', props.result);
}
</script>

<template>
  <EnrichmentCheckingPill v-if="state === 'loading'" data-test-id="enrichment-chip-loading" />
  <component
    v-else-if="state !== 'hidden'"
    :is="isInteractive ? 'button' : 'div'"
    :type="isInteractive ? 'button' : undefined"
    :data-test-id="`enrichment-chip-${state}`"
    :title="tooltip"
    class="enrichment-chip"
    :class="{
      'enrichment-chip--positive': state === 'updated' || state === 'verified',
      'enrichment-chip--neutral': state === 'checked',
      'cursor-pointer': isInteractive,
    }"
    @click.stop="handleClick"
  >
    <span class="enrichment-chip__check" aria-hidden="true">
      <CheckIcon
        class="h-[9px] w-[9px]"
        viewBox="0 0 16 16"
        data-test-id="enrichment-chip-check-icon"
      />
    </span>
    <span class="min-w-0 truncate">{{ label }}</span>
  </component>
</template>

<style scoped lang="postcss">
.enrichment-chip {
  @apply inline-flex h-7 min-w-0 max-w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-full border-0;
  @apply py-0 pl-2 pr-[11px] text-xs font-650 leading-[18px];
  animation: enrichment-chip-resolve 450ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.enrichment-chip--positive {
  @apply bg-enrichment-positive text-primary-500;
}

.enrichment-chip--neutral {
  @apply bg-tint-40 text-shade-800;
}

.enrichment-chip__check {
  @apply inline-flex h-[15px] w-[15px] shrink-0 items-center justify-center rounded-full;
}

.enrichment-chip--positive .enrichment-chip__check {
  @apply bg-primary-500 text-white;
}

.enrichment-chip--neutral .enrichment-chip__check {
  @apply bg-tint-80;
}

@keyframes enrichment-chip-resolve {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .enrichment-chip {
    animation: none;
  }
}
</style>
